import { snapSecondsToStep } from '@/animation/timeline/snap'
import type { AnimationLayer } from '@/types/animationLayer'

/**
 * 计算播放头「磁吸」判定半径（秒）：与总时长略相关，并设上下限。
 *
 * @param timelineMaxSec - 时间轴总时长（秒）
 */
export function playheadSnapThresholdSec(timelineMaxSec: number): number {
  const max = Math.max(0.001, Number(timelineMaxSec))
  return Math.max(0.05, Math.min(0.28, max * 0.012))
}

/**
 * 收集可吸附时刻：0、总时长、各 clip 起止、各关键帧全局时间。
 *
 * @param layers - 动画图层列表
 * @param timelineMaxSec - 时间轴总时长上限
 */
export function collectPlayheadSnapTargets(
  layers: readonly AnimationLayer[],
  timelineMaxSec: number,
): number[] {
  const max = Math.min(600, Math.max(0.001, timelineMaxSec))
  const set = new Set<number>([0, max])
  for (const layer of layers) {
    for (const clip of layer.clips) {
      set.add(Math.min(max, Math.max(0, clip.startSec)))
      set.add(Math.min(max, Math.max(0, clip.startSec + clip.durationSec)))
      for (const kf of clip.keyframes) {
        set.add(Math.min(max, Math.max(0, clip.startSec + kf.timeInClip)))
      }
    }
  }
  return [...set].sort((a, b) => a - b)
}

/**
 * 若存在与 `value` 距离不超过 `thresholdSec` 的目标，则吸附到最近目标；否则返回原值。
 */
export function snapToNearestTarget(
  value: number,
  targets: readonly number[],
  thresholdSec: number,
): number {
  let best: number | null = null
  let bestD = Infinity
  for (const t of targets) {
    const d = Math.abs(t - value)
    if (d <= thresholdSec && d < bestD) {
      bestD = d
      best = t
    }
  }
  return best !== null ? best : value
}

/**
 * 播放头最终时间：优先关键帧 / clip 边 / 0、总时长磁吸；未命中时再按网格步长对齐。
 *
 * @param rawSec - 指针换算出的秒数
 * @param layers - 图层（用于关键帧与 clip）
 * @param timelineMaxSec - 总时长
 * @param gridStepSec - 时间轴网格步长；`0` 表示仅用磁吸
 */
export function snapPlayheadForUi(
  rawSec: number,
  layers: readonly AnimationLayer[],
  timelineMaxSec: number,
  gridStepSec: number,
): number {
  const max = Math.min(600, Math.max(0.001, timelineMaxSec))
  const v = Math.min(max, Math.max(0, Number(rawSec)))
  const targets = collectPlayheadSnapTargets(layers, max)
  const thresh = playheadSnapThresholdSec(max)
  const magnet = snapToNearestTarget(v, targets, thresh)
  if (Math.abs(magnet - v) > 1e-6) return magnet
  const g = Number(gridStepSec)
  if (Number.isFinite(g) && g > 0) return Math.min(max, Math.max(0, snapSecondsToStep(v, g)))
  return v
}
